import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import { Box, DialogTitle, IconButton, Typography } from '@mui/material'

import { CreativeAdNetworkSelector } from '../CreativeAdNetworkSelector/CreativeAdNetworkSelector'

interface UAInsightsDialogTitleProps {
  title: ReactNode
  onClose: () => void
  canGoBack?: boolean
  onGoBack?: () => void
  showNetworkSelector?: boolean
  /**
   * Applied to the title text so the `Dialog` can point `aria-labelledby` at
   * it. Sits on the text rather than the `DialogTitle` row, which also holds
   * the back/close buttons and the network selector — naming the dialog after
   * all of those would read as noise to a screen reader.
   */
  titleId?: string
}

export const UAInsightsDialogTitle: FC<UAInsightsDialogTitleProps> = ({ title, onClose, canGoBack, onGoBack, showNetworkSelector, titleId }) => {
  const { t } = useTranslation('ua-insights')

  return (
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, gap: 2 }}>
      <Typography id={titleId} variant="h6" component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {showNetworkSelector && <CreativeAdNetworkSelector size="small" />}
        {canGoBack && onGoBack && (
          <IconButton aria-label={t('go_back')} onClick={onGoBack} size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <IconButton aria-label={t('close_dialog')} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
  )
}
